





























import {
  computed,
  defineComponent,
  PropType,
  ref,
  onMounted,
  onBeforeUnmount,
} from '@vue/composition-api'
import AtomButton from '@/components/atoms/AtomButton.vue'
import i18n from '@/locales/I18n'
import type { RewardDataType } from '@/store/hook/videoPlatformContents/useRewards'
import LimitedVideoModalSection from '@/components/HomePage/HomePane/LimitedVideoModalSection.vue'
import InAppBrowserWrapper, { InAppBrowserResult } from '@/util/inAppBrowser/InAppBrowserWrapper'
import InAppYoutubeBrowser from '@/util/inAppBrowser/InAppYoutubeBrowser'
import useDownload from '@/util/download/useDownload'
import MessageDialogStore from '@/store/stores/pageStore/common/MessageDialogStore'
import DeviceInfo from '@/util/DeviceInfo'
import CloudFrontUtil from '@/util/aws/CloudFrontUtil'
import FileExtensionUtil from '@/util/FileExtensionUtil'

export default defineComponent({
  name: 'RewardItemSection',
  components: {
    AtomButton,
    LimitedVideoModalSection,
  },
  props: {
    reward: {
      type: Object as PropType<RewardDataType>,
      required: true,
    },
  },
  setup(props) {
    const { loadFileToDownload, loadFileToDownloadForCordova } = useDownload()

    // 長押し判定の時間（ミリ秒）
    let pressTimer: number | null = null
    let messageAreaElement: Element | null = null

    // 長押し開始時の処理
    const startPress = () => {
      pressTimer = window.setTimeout(() => {
        // テキスト選択範囲を設定
        const selection = window.getSelection()
        if (selection && messageAreaElement) {
          selection.removeAllRanges()
          const range = document.createRange()
          range.selectNodeContents(messageAreaElement)
          selection.addRange(range)
        }
      }, 500)
    }

    // 長押しキャンセル時の処理（タッチ終了・移動時）
    const cancelPress = () => {
      if (pressTimer) {
        clearTimeout(pressTimer)
        pressTimer = null
      }
    }

    /**
     * マウント時に特典説明文の長押しでテキスト選択を有効化する
     * ユーザーが説明文をコピーできるようにするための処理
     */
    onMounted(() => {
      messageAreaElement = document.querySelector(`#Message-${props.reward.videoContentsId}`)
      if (!messageAreaElement) return

      // タッチイベントリスナーを登録
      messageAreaElement.addEventListener('touchstart', startPress)
      messageAreaElement.addEventListener('touchend', cancelPress)
      messageAreaElement.addEventListener('touchmove', cancelPress)
    })

    /**
     * アンマウント時のクリーンアップ処理
     */
    onBeforeUnmount(() => {
      // 残っているタイマーをクリア
      if (pressTimer) {
        clearTimeout(pressTimer)
        pressTimer = null
      }

      // イベントリスナーを削除
      if (messageAreaElement) {
        messageAreaElement.removeEventListener('touchstart', startPress)
        messageAreaElement.removeEventListener('touchend', cancelPress)
        messageAreaElement.removeEventListener('touchmove', cancelPress)
      }
    })

    // YouTubeをInAppBrowserで開く
    const inAppBrowser = new InAppYoutubeBrowser()

    // YouTubeモーダルを開くかどうか
    const videoModalEnabled = ref(false)

    // 特典動画かどうか
    const isVideo = computed(
      () =>
        props.reward.videoType.startsWith('rewardVideo') ||
        props.reward.videoType === 'userSpecifiedRewardVideo',
    )

    const buttonLabel = computed(() =>
      isVideo.value
        ? i18n.tc('RewardPage.buttonLabel.video')
        : i18n.tc('RewardPage.buttonLabel.image'),
    )

    const deadlineLabel = computed(() =>
      isVideo.value ? i18n.tc('RewardPage.deadline.video') : i18n.tc('RewardPage.deadline.image'),
    )

    const signedImagePath = computed(() => CloudFrontUtil.getSignedUrl(props.reward.imagePath))

    /**
     * YouTubeを再生する
     */
    const playYouTube = () => {
      if (InAppBrowserWrapper.isAvailable()) {
        // アプリで動作している場合、InAppBrowserを利用してYoutube動画を再生する
        inAppBrowser.showYoutube(props.reward.videoId, async (result: InAppBrowserResult) => {
          if (result?.isNetworkError) {
            await MessageDialogStore.value.open({
              title: i18n.t('apiNotification.dialog.0.title') as string,
              message: i18n.t('apiNotification.dialog.0.message') as string,
            })
          } else if (result?.isError) {
            await MessageDialogStore.value.open({
              title: i18n.t('RewardPage.errors.VideoPlayError.title') as string,
              message: i18n.t('RewardPage.errors.VideoPlayError.message') as string,
            })
          }
        })
      } else {
        videoModalEnabled.value = true
      }
    }

    /**
     * 動画モーダルを閉じる
     */
    const hideVideoModal = () => {
      videoModalEnabled.value = false
    }

    /**
     * 画像をダウンロードする
     */
    const downloadImage = async () => {
      if (DeviceInfo.isCordova()) {
        const downloadResult = await loadFileToDownloadForCordova(signedImagePath.value)
        if (downloadResult?.saveImage?.isSuccess === false) {
          if (downloadResult.saveImage.response.errorCode === 'vp_110_008') {
            MessageDialogStore.value.open({
              title: i18n.tc('RewardPage.errors.downloadImagePermissionError.title'),
              message: i18n.tc('RewardPage.errors.downloadImagePermissionError.message'),
            })
          } else {
            MessageDialogStore.value.open({
              title: i18n.tc('RewardPage.errors.downloadImageError.title'),
              message: i18n.tc('RewardPage.errors.downloadImageError.message'),
            })
          }
        } else if (downloadResult?.loadFile && downloadResult?.loadFile?.status !== 200) {
          MessageDialogStore.value.open({
            title: i18n.tc('RewardPage.errors.loadImageError.title'),
            message: i18n.tc('RewardPage.errors.loadImageError.message'),
          })
        } else {
          MessageDialogStore.value.open({
            title: i18n.tc('RewardPage.success.downloadImage.title'),
            message: i18n.tc('RewardPage.success.downloadImage.message'),
          })
        }
      } else {
        // 画像パスを見て、拡張子をつける
        const extension = FileExtensionUtil.getFileExtension(props.reward.imagePath)
        const downloadResult = await loadFileToDownload(
          `SFgo_${props.reward.title.en}.${extension}`,
          signedImagePath.value,
          {
            responseType: 'blob',
            headers: {
              accept: 'image/*',
            },
          },
        )
        if (!downloadResult) {
          MessageDialogStore.value.open({
            title: i18n.tc('RewardPage.errors.loadImageError.title'),
            message: i18n.tc('RewardPage.errors.loadImageError.message'),
          })
        } else {
          MessageDialogStore.value.open({
            title: i18n.tc('RewardPage.success.downloadImage.title'),
            message: i18n.tc('RewardPage.success.downloadImage.message'),
          })
        }
      }
    }

    const handleClick = () => {
      if (isVideo.value) {
        playYouTube()
      } else {
        downloadImage()
      }
    }

    return {
      buttonLabel,
      deadlineLabel,
      videoModalEnabled,
      signedImagePath,
      hideVideoModal,
      handleClick,
    }
  },
})
